import type {
  CreatorPlacement,
  CreatorCollaborationPackage
} from '@/biz/multiPlatformCollaboration/model';
import type { CreatorAudienceViewV2 } from '@/interface/creatorOrderV2';

export const isCreatorAudienceAnalysisUnsupported = (
  audience?: CreatorAudienceViewV2 | null
) => audience?.source === 'none' && audience.reason === 'unsupported';

type CreatorAudienceUploadTargetBase = {
  orderId: string;
  platform: string;
  userName?: string;
  refreshOrderId: string;
};

export type CreatorAudienceUploadTarget = CreatorAudienceUploadTargetBase &
  ({ kind: 'v2'; channelKey: string } | { kind: 'legacy' });

/** V2 targets a channelKey; legacy OCR requires a matching order identity. */
export function getCreatorAudienceUploadTarget(
  pkg: CreatorCollaborationPackage,
  placement: CreatorPlacement
): CreatorAudienceUploadTarget | null {
  const order = pkg.raw;
  if (Array.isArray(order.channels)) {
    const channelKey = placement.channelKey;
    if (!pkg.id || !channelKey) return null;
    return {
      kind: 'v2',
      channelKey,
      orderId: pkg.id,
      platform: placement.platform,
      userName: placement.userName,
      refreshOrderId: pkg.id
    };
  }

  const identity = order.audienceUploadIdentity;
  // Historical details may expose the representative influencer separately.
  // Match its actual platform ID, never pick the first channel or another account.
  if (
    identity?.platformId &&
    placement.platformId &&
    identity.platform === placement.platform &&
    String(identity.platformId) === String(placement.platformId)
  ) {
    return {
      kind: 'legacy',
      orderId: pkg.id,
      platform: placement.platform,
      userName: placement.userName,
      refreshOrderId: pkg.id
    };
  }
  if (
    order.influencerId == null ||
    placement.raw?.influencerId == null ||
    String(order.influencerId) !== String(placement.raw.influencerId) ||
    order.platform !== placement.platform
  )
    return null;
  return {
    kind: 'legacy',
    orderId: pkg.id,
    platform: placement.platform,
    userName: placement.userName,
    refreshOrderId: pkg.id
  };
}

export const getCreatorChannelAudience = (
  placement: CreatorPlacement,
  pkg?: CreatorCollaborationPackage,
  audienceViews?: CreatorAudienceViewV2[]
) => {
  if (Array.isArray(audienceViews)) {
    // A V2 audience belongs to its exact channelKey, never its array index or platform.
    return (
      audienceViews.find((view) => view.channelKey === placement.channelKey) ??
      null
    );
  }
  return (
    placement.raw?.audienceView ?? placement.raw?.analysis?.audienceView ?? null
  );
};

export const hasCreatorChannelAudience = (
  placement: CreatorPlacement,
  pkg?: CreatorCollaborationPackage,
  audienceViews?: CreatorAudienceViewV2[]
) => {
  const audience = getCreatorChannelAudience(placement, pkg, audienceViews);
  return Boolean(
    audience &&
      (audience.source === 'first_party' ||
        (audience.source === 'self_estimated' && audience.status === 'success'))
  );
};

/** Match the legacy quote prerequisite, scoped to the selected active channels. */
export const hasCreatorIncludedAudienceData = (
  pkg: CreatorCollaborationPackage,
  includedPlacementIds: string[],
  audienceViews?: CreatorAudienceViewV2[]
) => {
  const included = new Set(includedPlacementIds);
  const placements = pkg.placements.filter(
    (placement) => included.has(placement.id) && placement.raw?.active !== false
  );
  return (
    placements.length > 0 &&
    placements.every((placement) =>
      hasCreatorChannelAudience(placement, pkg, audienceViews)
    )
  );
};
